/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CoverageBucket,
  ListManagedDevicesRequest,
  ListManagedDevicesSecurity,
} from "../models/operations/listmanageddevices.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildManagedDevicesInfiniteQuery,
  buildManagedDevicesQuery,
  ManagedDevicesInfiniteQueryData,
  ManagedDevicesPageParams,
  ManagedDevicesQueryData,
  prefetchManagedDevices,
  prefetchManagedDevicesInfinite,
  queryKeyManagedDevices,
  queryKeyManagedDevicesInfinite,
} from "./managedDevices.core.js";
export {
  buildManagedDevicesInfiniteQuery,
  buildManagedDevicesQuery,
  type ManagedDevicesInfiniteQueryData,
  type ManagedDevicesPageParams,
  type ManagedDevicesQueryData,
  prefetchManagedDevices,
  prefetchManagedDevicesInfinite,
  queryKeyManagedDevices,
  queryKeyManagedDevicesInfinite,
};

export type ManagedDevicesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listManagedDevices deviceIntegrations
 *
 * @remarks
 * Page through the org's synced MDM device inventory, newest first, with each device's coverage bucket.
 */
export function useManagedDevices(
  request?: ListManagedDevicesRequest | undefined,
  security?: ListManagedDevicesSecurity | undefined,
  options?: QueryHookOptions<ManagedDevicesQueryData, ManagedDevicesQueryError>,
): UseQueryResult<ManagedDevicesQueryData, ManagedDevicesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildManagedDevicesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listManagedDevices deviceIntegrations
 *
 * @remarks
 * Page through the org's synced MDM device inventory, newest first, with each device's coverage bucket.
 */
export function useManagedDevicesSuspense(
  request?: ListManagedDevicesRequest | undefined,
  security?: ListManagedDevicesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ManagedDevicesQueryData,
    ManagedDevicesQueryError
  >,
): UseSuspenseQueryResult<ManagedDevicesQueryData, ManagedDevicesQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildManagedDevicesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listManagedDevices deviceIntegrations
 *
 * @remarks
 * Page through the org's synced MDM device inventory, newest first, with each device's coverage bucket.
 */
export function useManagedDevicesInfinite(
  request?: ListManagedDevicesRequest | undefined,
  security?: ListManagedDevicesSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    ManagedDevicesInfiniteQueryData,
    ManagedDevicesQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<ManagedDevicesInfiniteQueryData, ManagedDevicesPageParams>,
  ManagedDevicesQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    ManagedDevicesInfiniteQueryData,
    ManagedDevicesQueryError,
    InfiniteData<ManagedDevicesInfiniteQueryData, ManagedDevicesPageParams>,
    QueryKey,
    ManagedDevicesPageParams
  >({
    ...buildManagedDevicesInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listManagedDevices deviceIntegrations
 *
 * @remarks
 * Page through the org's synced MDM device inventory, newest first, with each device's coverage bucket.
 */
export function useManagedDevicesInfiniteSuspense(
  request?: ListManagedDevicesRequest | undefined,
  security?: ListManagedDevicesSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    ManagedDevicesInfiniteQueryData,
    ManagedDevicesQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<ManagedDevicesInfiniteQueryData, ManagedDevicesPageParams>,
  ManagedDevicesQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    ManagedDevicesInfiniteQueryData,
    ManagedDevicesQueryError,
    InfiniteData<ManagedDevicesInfiniteQueryData, ManagedDevicesPageParams>,
    QueryKey,
    ManagedDevicesPageParams
  >({
    ...buildManagedDevicesInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setManagedDevicesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      provider?: string | undefined;
      coverageBucket?: CoverageBucket | undefined;
      userIds?: Array<string> | undefined;
      userEmails?: Array<string> | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: ManagedDevicesQueryData,
): ManagedDevicesQueryData | undefined {
  const key = queryKeyManagedDevices(...queryKeyBase);

  return client.setQueryData<ManagedDevicesQueryData>(key, data);
}

export function invalidateManagedDevices(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      provider?: string | undefined;
      coverageBucket?: CoverageBucket | undefined;
      userIds?: Array<string> | undefined;
      userEmails?: Array<string> | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "deviceIntegrations",
      "listManagedDevices",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllManagedDevices(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "deviceIntegrations", "listManagedDevices"],
  });
}
